// ProcessImmediateEnquiryModal — quick-convert flow for enquiries
// whose job type carries process_immediately=1 (legacy:
// scrap_collection, load_on_site). The user picks an inventory unit
// and (optionally) records price + payment, then the server converts
// the enquiry to a job and auto-advances it to the terminal stage in
// the same transaction.
//
// Server contract: POST /api/v1/skip/enquiries/:enc_id/convert
// Body: { skip_inventory_enc_id, notes?, price?, payment_status?,
//         payment_method? }
// On success the response carries the new job's enc_id which the
// caller uses to navigate to /skip/jobs/:enc_id.
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../../components/ui/Button/Button';
import { Modal } from '../../../../components/ui/Modal/Modal';
import { useConvertSkipEnquiry } from '../../hooks/useSkipEnquiries';
import { useSkipInventory } from '../../hooks/useSkipInventory';
import { extractErrorMessage } from '../../../../utils/errorMessage';

import styles from './ProcessImmediateEnquiryModal.module.css';

export interface ProcessImmediateEnquiryModalProps {
  isOpen: boolean;
  enquiryEncId: string;
  enquiryRef: string;
  customerName: string;
  jobTypeName: string;
  /** Bug 3 — enc_id of the parent enquiry's chosen skip_stock_sizes
   *  row, when one is set. The inventory dropdown filters by this so
   *  only matching units are shown. Null when the enquiry was
   *  captured without a size — the picker stays disabled with a hint. */
  skipSizeEncId: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

type PaymentStatus = 'unpaid' | 'paid' | 'partial';
type PaymentMethod = '' | 'cash' | 'card' | 'bank_transfer' | 'account';

export function ProcessImmediateEnquiryModal({
  isOpen,
  enquiryEncId,
  enquiryRef,
  customerName,
  jobTypeName,
  skipSizeEncId,
  onClose,
  onSuccess,
}: ProcessImmediateEnquiryModalProps): JSX.Element | null {
  const navigate = useNavigate();
  // Bug 3 — server-filter the inventory list by the enquiry's chosen
  // skip size. When no size is set we still call the endpoint (the
  // picker stays disabled below); fetching the unfiltered list keeps
  // the cache key stable across modals if the enquiry later picks a
  // size.
  const inventoryQuery = useSkipInventory(
    skipSizeEncId !== null
      ? { status: 'available', limit: 200, skip_size_enc_id: skipSizeEncId }
      : { status: 'available', limit: 200 },
  );
  const convert = useConvertSkipEnquiry(enquiryEncId);

  const [skipInventoryEncId, setSkipInventoryEncId] = useState('');
  const [notes, setNotes] = useState('');
  const [price, setPrice] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('unpaid');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('');
  const [error, setError] = useState<string | null>(null);

  const inventoryOptions = useMemo(
    () => inventoryQuery.data?.inventory ?? [],
    [inventoryQuery.data],
  );

  if (!isOpen) return null;

  function close(): void {
    if (convert.isPending) return;
    setError(null);
    onClose();
  }

  function handleSubmit(): void {
    setError(null);
    if (!skipInventoryEncId) {
      setError('Please select a stock unit.');
      return;
    }
    const parsedPrice = price.trim() === '' ? null : Number(price);
    if (parsedPrice !== null && Number.isNaN(parsedPrice)) {
      setError('Price must be a valid number.');
      return;
    }
    convert.mutate(
      {
        skip_inventory_enc_id: skipInventoryEncId,
        notes: notes.trim() || null,
        price: parsedPrice,
        payment_status: paymentStatus,
        payment_method: paymentMethod || null,
      },
      {
        onSuccess: (data) => {
          onSuccess?.();
          navigate(`/skip/jobs/${encodeURIComponent(data.job.enc_id)}`);
        },
        onError: (err) => setError(extractErrorMessage(err)),
      },
    );
  }

  return (
    <Modal
      open
      onClose={close}
      title={`Process — ${jobTypeName}`}
      size="md"
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" onClick={close} disabled={convert.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={convert.isPending || !skipInventoryEncId}
          >
            {convert.isPending ? 'Processing…' : 'Process Now'}
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        <p className={styles.note}>
          Enquiry <strong>{enquiryRef}</strong> for <strong>{customerName}</strong> will
          be converted to a job and marked as completed immediately.
        </p>

        <label className={styles.field}>
          <span className={styles.label}>Stock Unit *</span>
          <select
            className={styles.select}
            value={skipInventoryEncId}
            onChange={(e) => setSkipInventoryEncId(e.target.value)}
            disabled={skipSizeEncId === null}
          >
            <option value="">
              {skipSizeEncId === null
                ? '— Set a stock size on the enquiry first —'
                : '— Select a stock unit —'}
            </option>
            {inventoryOptions.map((u) => {
              const label = [
                u.skip_number,
                u.stock_type_name,
                u.size_name,
              ]
                .filter((s) => typeof s === 'string' && s.length > 0)
                .join(' — ');
              return (
                <option key={u.enc_id} value={u.enc_id}>
                  {label}
                </option>
              );
            })}
          </select>
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Notes (optional)</span>
          <textarea
            className={styles.textarea}
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={2000}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Agreed price (optional)</span>
          <input
            className={styles.input}
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </label>

        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Payment status</span>
            <select
              className={styles.select}
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value as PaymentStatus)}
            >
              <option value="unpaid">Unpaid</option>
              <option value="paid">Paid</option>
              <option value="partial">Partial</option>
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Payment method</span>
            <select
              className={styles.select}
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
            >
              <option value="">—</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="account">Account</option>
            </select>
          </label>
        </div>

        {error ? <p className={styles.error}>{error}</p> : null}
      </div>
    </Modal>
  );
}
